import React from "react";
import { Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { ISchema, ISchemaProperty } from "../Schema";
import { PropertyTypes } from "../../../../enums/PropertyTypes";
import FormSelectWrapper from "./components/FormSelectWrapper";
import FormDisposalCheckOperatorSelect from "./components/FormDisposalCheckOperatorSelect";
import FormTextInputWrapper from "./components/FormTextInputWrapper";
import FormYearInputWrapper from "./components/FormYearInputWrapper";
import FormAccountRole from "./components/FormAccountRole";
import { Button, Form } from "react-aria-components";
import FormAddress from "./components/FormAddress";
import FormContactDetails from "./components/FormContactDetails";
import FormNumberInputWrapper from "./components/FormNumberInputWrapper";
import FormComputed from "./components/FormComputed";
import FormSectionSelectWrapper from "./components/FormSectionSelectWrapper";
import SubmitButton from "../../SubmitButton";
import FormToggleWrapper from "./components/FormToggleWrapper";
import FormTextAreaWrapper from "./components/FormTextAreaWrapper";
import FormFileInputWrapper from "./components/FormFileInputWrapper";
import iconError from "../../../../assets/icons/icon-exclamation-red.svg"
import iconWarning from "../../../../assets/icons/icon-exclamation-grey.svg"
import { globalCanSubmitSignal, globalLoadingSignal, globalSubmittingSignal } from "../../layout/Layout";
import { useSignals } from "@preact/signals-react/runtime";
import FormOwner from "./components/FormOwner";
import FormNumberStepperWrapper from "./components/FormNumberStepperWrapper";
import FormSpecimens from "./components/FormSpecimens";
import FormTextReadonlyWrapper from "./components/FormTextReadonlyWrapper";
import FormAccountPrefix from "./components/FormAccountPrefix";
import FormDisposalYears from "./components/FormDisposalYears";
import Size from "../../../../enums/Size";
import FormDatePicker from "./components/FormDatePicker";
import { EntityNames } from "../../../../enums/EntityNames";
import FormVNumber from "./components/FormVNumber";
import { Change } from "./Form";
import FormAutoFill from "./components/FormAutoFill";

export interface IFormRenderer {
    entityName: string,
    parentId: string,
    parentValues: Record<string, any> | undefined,
    schema: ISchema
    values?: any,
    onChange: Function,
    onChangeBatched: (changes: Change[]) => void,
    onComplete: Function,
    onSubmit: any,
    hideButtons?: boolean,
    inlineError?: string,
    isCreate: boolean
    isInline?: boolean | undefined,
    submitOnly: boolean | undefined,
    onCancel?: Function,
    formWrapperClassName?: string | undefined,
    formActionsClassName?: string | undefined,
    formButtonsClassName?: string | undefined,
    formButtonSize?: Size | undefined,
    register?: UseFormRegister<Record<string, any>>,
    control?: Control<Record<string, any>>,
    setValue?: UseFormSetValue<Record<string, any>>
}

export default function FormRenderer({ formWrapperClassName, formActionsClassName, formButtonsClassName, formButtonSize, parentValues, parentId, schema, values, onChange, onChangeBatched, onComplete, onSubmit, onCancel, hideButtons, inlineError, isCreate, isInline, submitOnly, entityName, register, control, setValue }: IFormRenderer) {
    useSignals();

    var groupBy = function (xs: any, key: any) {
        return xs.reduce(function (rv: any, x: any) {
            (rv[x[key]] = rv[x[key]] || []).push(x);
            return rv;
        }, {});
    };

    // Filter props: show only if showInForm is true, and respect addOnly/editOnly flags
    var filteredProps = schema.props.filter(x => {
        if (!x.showInForm) return false;
        if (x.addOnly === true && !isCreate) return false;
        if (x.editOnly === true && isCreate) return false;
        return true;
    });

    var groupedProps = groupBy(filteredProps, 'group');

    function hasGroupName(group: string) {
        const groupName = groupedProps[group][0].group
        if (typeof groupName === "string") return true
        else return false
    }

    function cancel() {
        if (isInline && onCancel) {
            onCancel();
        } else {
            window.history.back();
        }
    }

    function getFormButtonsClassName(isInline: boolean | undefined, formButtonsClassName: string | undefined): string {
        if (formButtonsClassName) {
            return formButtonsClassName;
        }
        const classes = ['display--flex', 'gap--xs'];
        classes.push(`justifyContent--${isInline ? 'flex-end' : 'center'}`);
        if (isInline) {
            classes.push('padding__right--xs', 'padding__bottom--xs');
        }
        return classes.join(' ');
    }

    function getButtonSizeModifier(): Size {
        return formButtonSize ?? ('large' as unknown as Size);
    }

    return (
        <div className={`display--flex alignItems--center flex__direction--column ${(isInline ? '' : 'gap--s')}`}>
            <FormAutoFill 
                props={filteredProps} 
                values={values} 
                isCreate={isCreate} 
                parentValues={parentValues} 
            />
            
            <div className={`form__wrapper form__wrapper${(isInline ? '--inline' : '')} ${formWrapperClassName ? formWrapperClassName : ''}`}>
                {globalLoadingSignal.value.isBusy && <div className="loaders loaders__panel form__inline-loader"></div>}
                {!globalLoadingSignal.value.isBusy &&
                    <Form onSubmit={onSubmit} >
                        <div className={"grid-x grid-padding-x"}>
                            {
                                groupedProps?.undefined.map((prop: ISchemaProperty, l: number) => {
                                    const element = GetFormElement(prop, values, onChange, onChangeBatched, onComplete, l, parentId, isCreate, parentValues, entityName, register, setValue);
                                    const shouldRender = !prop.renderBasedOnParentData || prop.renderBasedOnParentData(parentValues, isCreate);
                                    return element && shouldRender ? (
                                        <div key={l} className={`cell ${prop.className ?? "small-12"} margin__bottom--s`}>
                                            {element}
                                        </div>
                                    ) : null;
                                })
                            }
                        </div>
                        {
                            Object.keys(groupedProps).map((group: string, i: number) => {
                                if (group === "undefined") return null; // Skip invalid groups

                                const groupItems = groupedProps[group]?.map((prop: ISchemaProperty, l: number) => {
                                    const element = GetFormElement(prop, values, onChange, onChangeBatched, onComplete, l, parentId, isCreate, parentValues, entityName, register, setValue);
                                    const shouldRender = !prop.renderBasedOnParentData || prop.renderBasedOnParentData(parentValues, isCreate);

                                    return element && shouldRender ? (
                                        <div key={l} className={`cell ${prop.className ?? "small-12"} margin__bottom--s margin__top--xs`}>
                                            {element}
                                        </div>
                                    ) : null;
                                });

                                return (
                                    <React.Fragment key={i}>
                                        <div className={`grid-x grid-padding-x form__group ${i === Object.keys(groupedProps).length - 1 ? "no-hr" : ""}`}>
                                            {hasGroupName(group) && <h3 className="margin__top--xxs">{group}</h3>}
                                            {groupItems}
                                        </div>
                                    </React.Fragment>
                                );
                            })
                        }

                        {!hideButtons &&
                            <div className={formActionsClassName ? formActionsClassName : 'form__actions'}>
                                {!isCreate && schema.editWarning &&
                                    <div className="form__inline-warning">
                                        <div className="form__inline-warning-header">
                                            <img src={iconWarning} />
                                            <p className="form__inline-warning-header__text">Warning</p>
                                        </div>
                                        <p className="form__inline-warning__text">{schema.editWarning}</p>
                                    </div>
                                }
                                {inlineError &&
                                    <div className="form__inline-error">
                                        <div className="form__inline-error-header">
                                            <img src={iconError} />
                                            <p className="form__inline-error-header__text">Error</p>
                                        </div>
                                        <p className="form__inline-error__text">{inlineError}</p>
                                    </div>
                                }
                                <div className={getFormButtonsClassName(isInline, formButtonsClassName)}>
                                    {!submitOnly &&
                                        <Button isDisabled={globalSubmittingSignal.value.isBusy} type="button" className={`button button--secondary button--${getButtonSizeModifier()}`} onPress={cancel}>
                                            <p className="button-text">Cancel</p>
                                        </Button>
                                    }
                                    <SubmitButton isLoading={globalSubmittingSignal.value.isBusy}
                                        isDisabled={!globalCanSubmitSignal.value.allowed || globalSubmittingSignal.value.isBusy}
                                        idleText={!isCreate && schema.editButtonText
                                            ? schema.editButtonText
                                            : isCreate
                                                ? 'Add'
                                                : 'Save'}
                                        submittingText={`${isCreate ? 'Adding' : 'Saving'}`}
                                        size={formButtonSize} />
                                </div>
                            </div>
                        }
                    </Form>
                }
            </div>
        </div>
    )
}


function GetFormElement(prop: ISchemaProperty, values: any, onChange: Function, onChangeBatched: (changes: Change[]) => void, onComplete: any, key: number, parentId: string, isCreate: boolean, parentValues: Record<string, any> | undefined, entityName?: string, register?: UseFormRegister<Record<string, any>>, setValue?: UseFormSetValue<Record<string, any>>) {
    const handleChange = (val: any, name?: string) => {
        const fieldName = name ?? prop.name;
        if (setValue) setValue(fieldName, val);
        onChange(val, fieldName);
    };

    switch (prop.type) {
        case PropertyTypes.string: {
            return (
                <FormTextInputWrapper
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    value={values[prop.name]}
                    required={prop.requiredInForm ?? false}
                    validateRule={prop.validateRule}
                    validationError={prop.validationError}
                    autocomplete={prop.autocomplete}
                    name={prop.name}
                    inputType={prop.inputType}
                    register={register ? register(prop.name) : undefined}
                />
            )
        }
        case PropertyTypes.selectByEntityName: {
            // For disposal checks, use the custom component that fetches only active operators
            if (entityName === EntityNames.disposalChecks && prop.entityName === EntityNames.operators && prop.name === 'operatorId') {
                return (
                    <FormDisposalCheckOperatorSelect
                        value={values[prop.name]}
                        label={prop.displayName}
                        onChange={(val: any) => handleChange(val, prop.name)}
                        required={prop.requiredInForm ?? false} />
                )
            }
            // For locations, when editing, use the location's parentId from values instead of the parentId prop
            // The parentId prop is for creating new entities under a parent, but when editing we need the entity's own parent
            const effectiveParentId = prop.entityName === EntityNames.locations && !isCreate && values['parentId']
                ? values['parentId']
                : parentId;
            return (
                <FormSelectWrapper
                    value={values[prop.name]}
                    label={prop.displayName}
                    entityName={prop.entityName!}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false}
                    parentId={effectiveParentId} />
            )
        }
        case PropertyTypes.year: {
            return (
                <FormYearInputWrapper
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    value={values[prop.name] ?? undefined}
                    required={prop.requiredInForm ?? false}
                />
            )
        }
        case PropertyTypes.accountRoles: {
            return (
                <FormAccountRole
                    parentId={parentId}
                    value={values[prop.name] ?? []}
                    onChange={(val: any) => handleChange(val, prop.name)} />
            )
        }
        case PropertyTypes.address: {
            return (
                <FormAddress
                    value={values[prop.name] ?? {}}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false}
                    validateRule={prop.validateRule}
                    validationError={prop.validationError}
                    register={register}
                    fieldPrefix={prop.name}
                />
            )
        }
        case PropertyTypes.contactDetails: {
            return (
                <FormContactDetails
                    value={values[prop.name] ?? {}}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    register={register}
                    fieldPrefix={prop.name}
                />
            )
        }
        case PropertyTypes.number: {
            return (
                <FormNumberInputWrapper
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    value={values[prop.name] ?? ''}
                    required={prop.requiredInForm ?? false}
                    validateRule={prop.validateRule}
                    validationError={prop.validationError} />)
        }
        case PropertyTypes.int:
        case PropertyTypes.intNullable:
            {
                return (
                    <FormNumberInputWrapper
                        label={prop.displayName}
                        onChange={(val: any) => handleChange(val, prop.name)}
                        value={values[prop.name] ?? ''}
                        required={prop.requiredInForm ?? false}
                        validateRule={prop.validateRule}
                        validationError={prop.validationError} />)
            }
        case PropertyTypes.quantity: {
            return (
                <FormNumberStepperWrapper
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    value={values[prop.name] ?? ''}
                    required={prop.requiredInForm ?? false}
                    validateRule={prop.validateRule}
                    validationError={prop.validationError} />)
        }
        case PropertyTypes.computed: {
            const computeProps = prop.computeProperties?.map(x => values[x] ?? 0)
            const value = prop.computeFunction!(computeProps)

            return (
                <FormComputed
                    label={prop.displayName}
                    value={value} />
            )
        }
        case PropertyTypes.PackingBoxType: {
            return (
                <FormSectionSelectWrapper
                    value={values[prop.name]}
                    placeHolder={values.typeMessage}
                    label={prop.displayName}
                    entityName={prop.entityName!}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false}
                    isCreate={isCreate} />
            )
        }
        case PropertyTypes.bool: {
            return (
                <FormToggleWrapper
                    value={values[prop.name]}
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                />
            )
        }
        case PropertyTypes.textArea: {
            return (
                <FormTextAreaWrapper
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    value={values[prop.name] ?? ''}
                    required={prop.requiredInForm ?? false}
                    validateRule={prop.validateRule}
                    validationError={prop.validationError}
                    autocomplete={prop.autocomplete}
                    name={prop.name}
                    register={register ? register(prop.name) : undefined}
                />)
        }
        case PropertyTypes.fileUpload: {
            return (
                <FormFileInputWrapper
                    onChange={(val: any) => handleChange(val, prop.name)}
                    value={values ?? {}} />
            )
        }
        case PropertyTypes.status: {
            return (
                <FormSelectWrapper
                    value={values[prop.name]}
                    label={prop.displayName}
                    entityName={prop.entityName!}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false} />
            )
        }
        case PropertyTypes.owner: {
            return (
                <FormOwner
                    value={values[prop.name]}
                    label={prop.displayName}
                    entityName={prop.entityName!}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false} />
            )
        }
        case PropertyTypes.disposalYears: {
            return (
                <FormDisposalYears
                    value={values[prop.name]}
                    label={prop.displayName}
                    entityName={prop.entityName!}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false} />
            )
        }
        case PropertyTypes.accountPrefix: {
            return (
                <FormAccountPrefix
                    isCreate={isCreate}
                    values={values}
                    parentValues={parentValues}
                    onChangeBatched={onChangeBatched} />
            )
        }
        case PropertyTypes.specimens: {
            return (
                <FormSpecimens
                    value={values[prop.name]}
                    label={prop.displayName}
                    entityName={prop.entityName!}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false} />
            )
        }
        case PropertyTypes.readonlyString: {
            return (
                <FormTextReadonlyWrapper
                    value={values[prop.name]}
                    label={prop.displayName} />
            )
        }
        case PropertyTypes.dateOnly: {
            return (
                <FormDatePicker
                    value={values[prop.name]}
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    required={prop.requiredInForm ?? false} />
            )
        }
        case PropertyTypes.vNumberStandardSize: {
            return (
                <FormVNumber
                    value={values[prop.name] ?? null}
                    label={prop.displayName}
                    onChange={(val: any) => handleChange(val, prop.name)}
                    onChangeBatched={onChangeBatched}
                    widthValue={values["width"]}
                    heightValue={values["height"]}
                    depthValue={values["depth"]}
                    required={prop.requiredInForm ?? false} />
            )
        }
    }
}
